import { push } from '@lagunovsky/redux-react-router'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack } from '@mui/material'
import HomeAction from 'home/actions/HomeAction'
import { omit, orderBy, pickBy } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { EQUIPMENT_ID } from 'materiel/constants/MaterielConstants'
import { H_MAT_EQUIPMENT } from '../../../../account/constants/AccessRulesConstants'
import {
    MAT_ANTENNES,
    MAT_BATTERIES,
    MAT_CAPTEURS,
    MAT_CENTRALES,
    MAT_DIVERS,
    MAT_SIMS,
    MAT_TELECOMS,
} from '../../../../administration/components/user/constants/HabilitationConstants'
import MaterielCard from '../../../../components/card/Card'
import FilePanel from '../../../../components/file/FilePanel'
import NumberField from '../../../../components/forms/NumberField'
import SmallPicturePanel from '../../../../components/picture/SmallPicturePanel'
import UpdatePanel from '../../../../components/updatePanel/UpdatePanel'
import { componentHasHabilitations } from '../../../../utils/HabilitationUtil'
import { getMaterielIconValue } from '../../../../utils/MaterielUtils'
import { updateMaterialize } from '../../../../utils/MaterializeUtils'
import { getUser, hasHabilitation } from '../../../../utils/SettingUtils'
import { isSaveBlocked } from '../../../utils/MaterielUtils'
import AdministratorPanel from '../../administrator/AdministratorPanel'
import AssigmentChartPanel from '../../chart/AssigmentChartPanel'
import CentralEquipmentPanel from '../../central/components/CentralEquipmentPanel'
import ChannelPanel from '../../central/components/channel/ChannelPanel'
import RangePanel from '../../central/components/range/RangePanel'
import PowerSupplyEquipmentPanel from '../../powerSupply/components/PowerSupplyEquipmentPanel'
import SensorEquipmentPanel from '../../sensor/components/SensorEquipmentPanel'
import SimEquipmentPanel from '../../sim/components/SimEquipmentPanel'
import TelecomEquipmentPanel from '../../telecom/components/TelecomEquipmentPanel'
import VariousMaterielEquipmentPanel from '../../variousMateriel/components/VariousMaterielEquipmentPanel'
import AntennaPanel from '../../antenna/components/AntennaPanel'
import LastSituationMarkerPanel from '../../situation/LastSituationMarkerPanel'
import SituationPanel from '../../situation/SituationPanel'
import EquipmentAction from '../actions/EquipmentAction'
import CentralAction from '../../central/actions/CentralAction'
import PowerSupplyAction from '../../powerSupply/actions/PowerSupplyAction'
import TelecomAction from '../../telecom/actions/TelecomAction'
import SensorAction from '../../sensor/actions/SensorAction'
import VariousMaterielAction from '../../variousMateriel/actions/VariousMaterielAction'
import SimAction from '../../sim/actions/SimAction'
import AntennaAction from '../../antenna/actions/AntennaAction'
import EquipmentPanel from './EquipmentPanel'
import { CardTitle } from '../../../../components/card/NewCard'
import { darkBlue } from '../../../../utils/constants/ColorTheme'

const INITIAL_MATERIELS = {
    central: [],
    powerSupply: [],
    sensor: [],
    sim: [],
    telecom: [],
    variousMateriel: [],
    antenna: [],
}

const EquipmentApp = () => {
    const {
        equipment: storeEquipment,
        equipments,
        equipmentsLastSituations,
        equipmentTypes,
        equipmentSituations,
        equipmentPictures,
        equipmentFiles,
        accountUser,
        variousMaterielTypes,
        materielSettingRules,
    } = useSelector(store => ({
        equipment: store.EquipmentReducer.equipment,
        equipments: store.EquipmentReducer.equipments,
        equipmentsLastSituations: store.EquipmentReducer.equipmentsLastSituations,
        equipmentTypes: store.EquipmentReducer.equipmentTypes,
        equipmentSituations: store.EquipmentReducer.equipmentSituations,
        equipmentPictures: store.EquipmentReducer.equipmentPictures,
        equipmentFiles: store.EquipmentReducer.equipmentFiles,
        accountUser: store.AccountReducer.accountUser,
        variousMaterielTypes: store.VariousMaterielReducer.variousMaterielTypes,
        materielSettingRules: store.MaterielReducer.materielSettingRules,
    }))

    const dispatch = useDispatch()
    const params = useParams()

    const [equipment, setEquipment] = useState(() => ({ administrator: accountUser.contributorCode }))
    const [materiels, setMateriels] = useState(INITIAL_MATERIELS)
    const [isOpen, setIsOpen] = useState(false)
    const [isDuplicatePopinOpen, setIsDuplicatePopinOpen] = useState(false)
    const [equipmentNumber, setEquipmentNumber] = useState(1)
    const [isEditMode, setIsEditMode] = useState(false)

    const fetchEquipment = id => dispatch(EquipmentAction.fetchEquipment(id)).then(json => {
        if (json?.equipment?.equipmentId) {
            dispatch(EquipmentAction.fetchEquipmentSituations(json.equipment.equipmentId))
            dispatch(EquipmentAction.fetchEquipmentPictures(json.equipment.equipmentId))
            if (json.equipment.serialNumber) {
                dispatch(EquipmentAction.fetchEquipmentFiles(json.equipment.serialNumber))
            }
            setEquipment(json.equipment)
            setMateriels({
                central: json.central ?? [],
                powerSupply: json.powerSupply ?? [],
                sensor: json.sensor ?? [],
                sim: json.sim ?? [],
                telecom: json.telecom ?? [],
                variousMateriel: json.variousMateriel ?? [],
                antenna: json.antenna ?? [],
            })
        }
    })

    const changeAdministrator = administrator => setEquipment(prev => ({ ...prev, administrator }))

    const changeStateAttributeEquipment = changes => setEquipment(prev => ({ ...prev, ...changes }))

    const changeStateAttributeMat = (mat, index, changes) => {
        setMateriels(prev => ({
            ...prev,
            [mat]: prev[mat].map((d, i) => i === index ? { ...d, ...changes } : d),
        }))
    }

    const onDeleteMaterial = (mat, index) => {
        const newMat = materiels[mat].filter((_, i) => i !== index)
        setMateriels(prev => ({ ...prev, [mat]: newMat }))
        setEquipment(prev => ({ ...prev, [`${mat}Integrated`]: !!newMat.length }))
    }

    const deleteSituation = id => dispatch(EquipmentAction.deleteEquipmentSituation(id, params.id))

    const getEquipmentInformation = materials => {
        const {
            serialNumber,
            reference,
            manufacturerId,
            providerId,
            networkCode,
            manufactureDate,
            purchaseDate,
            withdrawalDate,
            administrator,
        } = equipment
        return materials.map(m => ({
            ...m,
            serialNumber,
            reference,
            manufacturerId,
            providerId,
            networkCode,
            manufactureDate,
            purchaseDate,
            withdrawalDate,
            administrator,
        }))
    }

    const getEquipmentWithMateriels = () => {
        const {
            centralIntegrated,
            sensorIntegrated,
            powerSupplyIntegrated,
            simIntegrated,
            telecomIntegrated,
            variousMaterielIntegrated,
            antennaIntegrated,
        } = equipment
        return pickBy({
            equipment,
            central: centralIntegrated ? getEquipmentInformation(materiels.central) : [],
            sensor: sensorIntegrated ? getEquipmentInformation(materiels.sensor) : [],
            powerSupply: powerSupplyIntegrated ? getEquipmentInformation(materiels.powerSupply) : [],
            sim: simIntegrated ? getEquipmentInformation(materiels.sim) : [],
            telecom: telecomIntegrated ? getEquipmentInformation(materiels.telecom) : [],
            variousMateriel: variousMaterielIntegrated ? getEquipmentInformation(materiels.variousMateriel) : [],
            antenna: antennaIntegrated ? getEquipmentInformation(materiels.antenna) : [],
        }, e => e)
    }

    const filterConsultant = actions => getUser().consultant === '1'
        ? omit(actions, ['save', 'edit', 'delete', 'replace'])
        : actions

    useActions(() => {
        updateMaterialize()
        if (params.id === 'new' && !storeEquipment?.equipmentId) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === EQUIPMENT_ID)
                    if (isSaveBlocked(equipments, equipment, rules, 'equipmentType')) return

                    dispatch(EquipmentAction.createEquipment(getEquipmentWithMateriels())).then(id => {
                        if (id) {
                            setIsEditMode(false)
                            const situation = {
                                idEquipment: id,
                                situationDate: equipment.purchaseDate,
                                statusCode: 2,
                            }
                            dispatch(EquipmentAction.saveEquipmentSituation(situation))
                        }
                    })
                },
                cancel: () => {
                    dispatch(push('/materiel/equipment'))
                },
            })
        }
        if (isEditMode) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === EQUIPMENT_ID)
                    if (isSaveBlocked(equipments, { ...equipment, id: equipment.equipmentId }, rules, 'equipmentType')) return

                    dispatch(EquipmentAction.updateEquipment(getEquipmentWithMateriels())).then(success => success && setIsEditMode(false))
                },
                cancel: () => {
                    setEquipment(storeEquipment)
                    setIsEditMode(false)
                },
            })
        }
        return filterConsultant({
            edit: () => {
                setIsEditMode(true)
            },
            duplicate: () => setIsDuplicatePopinOpen(true),
            delete: () => {
                dispatch(EquipmentAction.deleteEquipment(params.id))
            },
            replace: () => {
                dispatch(ToastrAction.info(i18n.inDeveloppmentFunctionnality))
            },
        })
    }, [isEditMode, equipment, materiels, storeEquipment, equipments, materielSettingRules])

    useTitle(() => {
        const { reference = '', serialNumber = '', equipmentType, id } = storeEquipment
        const title = reference || serialNumber || id
        const type = (equipmentTypes.find(t => t.code === parseInt(equipmentType)) || {}).label || i18n.notDefined
        return ([
            {
                title: i18n.materiels,
                href: 'materiel',
            },
            {
                title: i18n.equipments,
                href: 'materiel/equipment',
            },
            {
                title: type,
                href: `materiel/equipment/${params.id}`,
            },
            {
                title,
                href: `materiel/equipment/${params.id}`,
            },
        ])
    }, [storeEquipment, equipmentTypes])

    useEffect(() => {
        if (!componentHasHabilitations(H_MAT_EQUIPMENT)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
            return undefined
        }
        if (!equipmentTypes.length) {
            dispatch(EquipmentAction.fetchEquipmentTypes())
        }
        if (!equipments.length) {
            dispatch(EquipmentAction.fetchEquipments())
        }
        dispatch(CentralAction.fetchCentralTypes(true))
        dispatch(PowerSupplyAction.fetchPowerSupplyTypes(true))
        dispatch(SensorAction.fetchSensorTypes(true))
        dispatch(SimAction.fetchSimTypes(true))
        dispatch(TelecomAction.fetchTelecomTypes(true))
        if (!variousMaterielTypes.length) {
            dispatch(VariousMaterielAction.fetchVariousMaterielTypes())
        }
        dispatch(AntennaAction.fetchAntennaTypes())
        dispatch(HomeAction.setHelpLink('materiel', ''))
        return () => dispatch(EquipmentAction.resetEquipment())
    }, [])

    useEffect(() => {
        if (params.id !== 'new') {
            fetchEquipment(params.id)
            setIsEditMode(false)
        } else {
            setIsEditMode(true)
        }
    }, [params.id])

    const getMaterielNumber = ({ serialNumber, reference }) => serialNumber || reference || ''

    const getEquipmentLabel = eq => {
        const number = getMaterielNumber(eq)
        if (eq.equipmentType) {
            const type = equipmentTypes.find(t => t.id === eq.equipmentType)
            if (type) {
                return `${type.label} - ${number}`
            }
        }
        return number
    }

    const getEquipmentsFormat = () => equipments.map(eq => ({
        id: eq.id,
        label: getEquipmentLabel(eq),
    }))

    const getRangeAndChannelValues = central => {
        const lastSituation = orderBy(equipmentSituations, 'id', 'desc')[0]
        const lastSituationOnSite = !!equipmentSituations.length && !!lastSituation && lastSituation.statusCode === 1
        if (!lastSituationOnSite) {
            return null
        }
        const centralChannels = central.centralChannels.length ? central.centralChannels : central.centralChannelsDefault
        return (
            <>
                <RangePanel active={isEditMode} />
                <ChannelPanel
                    central={central}
                    active={isEditMode}
                    centralChannels={centralChannels}
                    refreshDataFunction={() => fetchEquipment(params.id)}
                />
            </>
        )
    }

    const onCloseDuplicatePopin = () => {
        setIsDuplicatePopinOpen(false)
        setEquipmentNumber(1)
    }

    const onDuplicate = () => {
        dispatch(EquipmentAction.duplicateEquipment(params.id, equipmentNumber)).then(onCloseDuplicatePopin)
    }

    const disabled = { active: isEditMode, disabled: !isEditMode }

    const renderMaterielCards = (mat, label, route, renderPanel) => {
        if (!equipment[`${mat}Integrated`]) {
            return null
        }
        return materiels[mat].map((item, i) => (
            <Card key={`${mat}-${i}`}>
                <CardTitle
                    title={label}
                    color={darkBlue}
                    actions={[
                        {
                            icon: 'reply',
                            color: 'white',
                            tooltip: i18n.export,
                            onClick: () => dispatch(push(`/materiel/${route}/${item.id}`)),
                        },
                        {
                            icon: 'close',
                            color: 'white',
                            tooltip: i18n.export,
                            onClick: () => onDeleteMaterial(mat, i),
                            displayed: isEditMode,
                        },
                    ]}
                />
                <CardContent>
                    {renderPanel(item, i)}
                </CardContent>
            </Card>
        ))
    }

    const cardActions = [
        {
            iconName: 'add',
            onClick: () => setIsOpen(true),
        },
    ]
    const displaySupply = !materiels.central.length
    const displayDesiccant = !materiels.sensor.length

    const materialType = [
        hasHabilitation(MAT_CENTRALES, false) && 'central',
        hasHabilitation(MAT_BATTERIES, false) && 'powerSupply',
        hasHabilitation(MAT_CAPTEURS, false) && 'sensor',
        hasHabilitation(MAT_SIMS, false) && 'sim',
        hasHabilitation(MAT_TELECOMS, false) && 'telecom',
        hasHabilitation(MAT_DIVERS, false) && 'variousMateriel',
        hasHabilitation(MAT_ANTENNES, false) && 'antenna',
    ].filter(t => !!t)

    return (
        <>
            <Card>
                <CardContent>
                    <Grid container size={12} spacing={2}>
                        <Grid container size={9}>
                            <LastSituationMarkerPanel materielSituation={equipmentSituations} />
                            <Grid container size={'grow'}>
                                <Grid size={12}>
                                    <EquipmentPanel
                                        id={params.id}
                                        equipment={equipment}
                                        onChange={changeStateAttributeEquipment}
                                        onChangeState={({ equipment: changedEquipment, ...rest }) => {
                                            setEquipment(prev => ({ ...prev, ...changedEquipment }))
                                            setMateriels(rest)
                                        }}
                                        {...disabled}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={3}>
                            <Stack spacing={1}>
                                <UpdatePanel
                                    updateDate={equipment.updateDate}
                                    updateLogin={equipment.updateLogin}
                                    jobExecId={equipment.jobExecutionId}
                                />
                                <Card>
                                    <CardContent>
                                        <AdministratorPanel
                                            onChange={changeAdministrator}
                                            selectedValue={equipment.administrator}
                                            isActive={isEditMode}
                                        />
                                    </CardContent>
                                </Card>
                                <AssigmentChartPanel materielSituation={equipmentSituations} />
                                <SmallPicturePanel pictures={equipmentPictures} />
                                <FilePanel files={equipmentFiles} />
                            </Stack>
                        </Grid>
                        <Grid size={12}>
                            <SituationPanel
                                materielSituation={equipmentSituations}
                                active={isEditMode}
                                idMateriel={storeEquipment.equipmentId}
                                serialNumber={storeEquipment.serialNumber}
                                purchaseDate={storeEquipment.purchaseDate}
                                idKey={'idEquipment'}
                                lastSituations={equipmentsLastSituations}
                                materials={getEquipmentsFormat()}
                                saveFunction={situation => dispatch(EquipmentAction.saveEquipmentSituation(situation))}
                                deleteFunction={deleteSituation}
                            />
                        </Grid>
                        <Grid size={12}>
                            <MaterielCard title={i18n.materiels} active={isEditMode} actions={cardActions}>
                                <Stack spacing={1} sx={{ padding: '1rem' }}>
                                    {renderMaterielCards('central', i18n.central, 'central', (c, i) => (
                                        <>
                                            <CentralEquipmentPanel
                                                central={c}
                                                onChange={changes => changeStateAttributeMat('central', i, changes)}
                                                displayDesiccant={displayDesiccant}
                                                {...disabled}
                                            />
                                            {getRangeAndChannelValues(c)}
                                        </>
                                    ))}
                                    {renderMaterielCards('powerSupply', i18n.powerSupply, 'powerSupply', (p, i) => (
                                        <PowerSupplyEquipmentPanel
                                            powerSupply={p}
                                            onChange={changes => changeStateAttributeMat('powerSupply', i, changes)}
                                            {...disabled}
                                        />
                                    ))}
                                    {renderMaterielCards('sensor', i18n.sensor, 'sensor', (s, i) => (
                                        <SensorEquipmentPanel
                                            sensor={s}
                                            displaySupply={displaySupply}
                                            onChange={changes => changeStateAttributeMat('sensor', i, changes)}
                                            {...disabled}
                                        />
                                    ))}
                                    {renderMaterielCards('sim', i18n.sim, 'sim', (s, i) => (
                                        <SimEquipmentPanel
                                            sim={s}
                                            onChange={changes => changeStateAttributeMat('sim', i, changes)}
                                            {...disabled}
                                        />
                                    ))}
                                    {renderMaterielCards('telecom', i18n.telecom, 'telecom', (t, i) => (
                                        <TelecomEquipmentPanel
                                            telecom={t}
                                            displaySupply={displaySupply}
                                            displayDesiccant={displayDesiccant}
                                            onChange={changes => changeStateAttributeMat('telecom', i, changes)}
                                            {...disabled}
                                        />
                                    ))}
                                    {renderMaterielCards('variousMateriel', i18n.variousMateriel, 'variousMateriel', (v, i) => (
                                        <VariousMaterielEquipmentPanel
                                            variousMateriel={v}
                                            onChange={changes => changeStateAttributeMat('variousMateriel', i, changes)}
                                            {...disabled}
                                        />
                                    ))}
                                    {renderMaterielCards('antenna', i18n.antenna, 'antenna', (a, i) => (
                                        <AntennaPanel
                                            antenna={a}
                                            onChange={changes => changeStateAttributeMat('antenna', i, changes)}
                                            {...disabled}
                                        />
                                    ))}
                                </Stack>
                            </MaterielCard>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <Dialog
                onClose={() => setIsOpen(false)}
                fullWidth
                maxWidth='md'
                open={isOpen}
            >
                <DialogTitle>{materialType.length ? i18n.pleaseChooseMaterielType : i18n.noTypeOfMaterialAvailable}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ padding: '1rem' }}>
                        {materialType.map(type => (
                            <Grid size={12} key={type}>
                                <div
                                    className='chip blue white-text flex justify-center align-center clickable'
                                    onClick={() => {
                                        const newMat = type === 'central' ? { centralChannels: [], centralChannelsDefault: [] } : {}
                                        setMateriels(prev => ({ ...prev, [type]: [...prev[type], newMat] }))
                                        setEquipment(prev => ({ ...prev, [`${type}Integrated`]: true }))
                                        setIsOpen(false)
                                    }}
                                >
                                    {getMaterielIconValue(type)}
                                    <h5 className='no-margin align-center' style={{ paddingLeft: '1%' }}>
                                        {i18n[type]}
                                    </h5>
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
            </Dialog>
            <Dialog
                onClose={onCloseDuplicatePopin}
                maxWidth='md'
                open={isDuplicatePopinOpen}
            >
                <DialogTitle>{i18n.howMuchEquipmentDoYouWantToCreate}</DialogTitle>
                <DialogContent>
                    <Grid container sx={{ padding: '1rem' }}>
                        <Grid size={6}>
                            <NumberField
                                value={equipmentNumber}
                                min={1}
                                onChange={v => setEquipmentNumber(v)}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCloseDuplicatePopin} variant='outlined' >
                        {i18n.cancel}
                    </Button>
                    <Button onClick={onDuplicate} variant='contained' color='primary'>
                        {i18n.validate}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default EquipmentApp
